import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { EditorHeader } from "./editor/EditorHeader";
import { EditorGeneralFields } from "./editor/EditorGeneralFields";
import { EditorDayItem } from "./editor/EditorDayItem";
import { EditorFooter } from "./editor/EditorFooter";

const LORA = { fontFamily: "'Lora', Georgia, serif" };

export default function AdminTripEditor({ tripId, destination, onClose, onSaved }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openDay, setOpenDay] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [regenDay, setRegenDay] = useState(null);

  useEffect(() => {
    axios.get(`${API}/trips/${tripId}`)
      .then(({ data }) => {
        if (data.result) setResult(JSON.parse(JSON.stringify(data.result)));
      })
      .catch(() => toast.error("Impossible de charger l'itinéraire"))
      .finally(() => setLoading(false));
  }, [tripId]);

  const setField = (field, value) => {
    setResult((r) => ({ ...r, [field]: value }));
    setDirty(true);
  };

  const setDayField = (dayIdx, field, value) => {
    setResult((r) => ({
      ...r,
      itineraire: r.itineraire.map((d, i) => (i === dayIdx ? { ...d, [field]: value } : d)),
    }));
    setDirty(true);
  };

  const regenerateDay = async (dayIdx) => {
    setRegenDay(dayIdx);
    try {
      const { data } = await axios.post(`${API}/admin/trips/${tripId}/regenerate-day`, { day_index: dayIdx });
      setResult((r) => ({
        ...r,
        itineraire: r.itineraire.map((d, i) => (i === dayIdx ? data.day : d)),
      }));
      setDirty(true);
      toast.success(`${data.day.jour} régénéré avec succès !`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la régénération");
    } finally {
      setRegenDay(null);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/trips/${tripId}/result`, { result });
      toast.success("Modifications sauvegardées !");
      setDirty(false);
      onSaved();
    } catch {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center overflow-y-auto py-6 sm:py-10 px-4"
      style={{ background: "rgba(43,38,35,0.65)" }}
      onClick={onClose}
    >
      <div
        className="flex flex-col w-full max-w-3xl"
        style={{ background: "#F9F6F0", borderRadius: "3px", border: "1px solid #DCD4C6" }}
        onClick={(e) => e.stopPropagation()}
      >
        <EditorHeader destination={destination} dirty={dirty} onClose={onClose} />

        <div className="px-6 py-5 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-7 h-7 border-2 border-[#C17F59] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : !result ? (
            <p className="text-center text-sm text-[#9A8F86] italic py-16" style={LORA}>
              L'itinéraire n'est pas encore disponible.
            </p>
          ) : (
            <>
              <EditorGeneralFields result={result} setField={setField} />
              <div>
                <p className="text-xs uppercase tracking-widest text-[#8B5E34] mb-3" style={LORA}>
                  Itinéraire jour par jour — {result.itineraire?.length || 0} jour(s)
                </p>
                <div className="space-y-1.5">
                  {(result.itineraire || []).map((day, i) => (
                    <EditorDayItem
                      key={i}
                      day={day}
                      dayIdx={i}
                      openDay={openDay}
                      setOpenDay={setOpenDay}
                      setDayField={setDayField}
                      regenerateDay={regenerateDay}
                      regenDay={regenDay}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        <EditorFooter onClose={onClose} onSave={save} saving={saving} dirty={dirty} result={result} />
      </div>
    </div>
  );
}
